import { useLiveQuery } from 'dexie-react-hooks';
import { useEffect, useMemo, useState } from 'react';
import { api } from './api';
import { isLocalOnly } from './local';
import { db } from './db';

/** Sales totals by hour: shared by Home and Reports (kept out of the Reports page so Home loads light). */
export interface HourRow { hour: number; bills: number; gross: number; discount: number; tax: number; net: number; cost?: number; by_pay: Record<string, number> }
export type Preset = 'today' | 'yesterday' | '7d' | '30d' | 'month' | 'lastMonth';

export function rangeFor(p: Preset): [number, number] {
  const start = new Date(); start.setHours(0, 0, 0, 0);
  const day = 86400000;
  const t = start.getTime();
  if (p === 'today') return [t, t + day];
  if (p === 'yesterday') return [t - day, t];
  if (p === '7d') return [t - 6 * day, t + day];
  if (p === 'month') return [new Date(start.getFullYear(), start.getMonth(), 1).getTime(), t + day];
  if (p === 'lastMonth') return [new Date(start.getFullYear(), start.getMonth() - 1, 1).getTime(), new Date(start.getFullYear(), start.getMonth(), 1).getTime()];
  return [t - 29 * day, t + day];
}

/** Server report (all terminals); falls back to this device's own bills when offline. */
// Last server answer per range: a revisit shows it at once and refreshes quietly (no skeleton, no jump).
const cache = new Map<string, HourRow[]>();

export function useSalesReport(from: number, to: number) {
  const key = `${from}:${to}`;
  const [hours, setHours] = useState<HourRow[] | null>(() => cache.get(key) ?? null);
  const [offline, setOffline] = useState(false);
  const local = useLiveQuery(() => db.bills.where('ts').between(from, to).toArray(), [from, to]);
  useEffect(() => {
    let live = true;
    // Phone-only (Starter): reports come from this phone's bills alone.
    if (isLocalOnly()) { setOffline(true); return; }
    setHours(cache.get(key) ?? null);
    api<{ hours: HourRow[] }>(`/api/reports/sales?from=${from}&to=${to}`)
      .then((r) => { cache.set(key, r.hours); if (live) { setHours(r.hours); setOffline(false); } })
      .catch(() => { if (live) setOffline(true); });
    return () => { live = false; };
  }, [from, to]); // eslint-disable-line react-hooks/exhaustive-deps
  const toRows = (bills: typeof local, base: HourRow[] = []) => {
    const m = new Map<number, HourRow>(base.map((r) => [r.hour, { ...r, by_pay: { ...r.by_pay } }]));
    for (const b of bills ?? []) {
      const h = Math.floor(b.ts / 3600000);
      const sign = b.kind === 1 ? -1 : 1;
      const r = m.get(h) ?? { hour: h, bills: 0, gross: 0, discount: 0, tax: 0, net: 0, by_pay: {} };
      r.bills += 1; r.gross += sign * b.subtotal; r.discount += sign * b.discount; r.tax += sign * b.tax; r.net += sign * b.total;
      for (const [k, v] of b.payments) r.by_pay[k] = (r.by_pay[k] ?? 0) + sign * v;
      m.set(h, r);
    }
    return [...m.values()].sort((a, b) => a.hour - b.hour);
  };
  // Server totals cover synced bills from every terminal; add this terminal's bills still in the outbox.
  const rows = useMemo(() => (offline || !hours ? toRows(local) : toRows(local?.filter((b) => !b.synced), hours)), [offline, hours, local]); // eslint-disable-line react-hooks/exhaustive-deps
  const sum = rows.reduce((s, r) => ({ bills: s.bills + r.bills, net: s.net + r.net, tax: s.tax + r.tax, discount: s.discount + r.discount }), { bills: 0, net: 0, tax: 0, discount: 0 });
  const byPay: Record<string, number> = {};
  for (const r of rows) for (const [k, v] of Object.entries(r.by_pay)) byPay[k] = (byPay[k] ?? 0) + v;
  return { rows, sum, byPay, offline, loading: !hours && !offline, local: local ?? [] };
}

